import type { BlockSerialized } from '#layers/block-engine/types/block';
import { BlockNotFoundError } from '#layers/block-engine/utils/block/errors';

export interface BlockAdapter {
	loadBlockSerialized: (id: string) => Promise<BlockSerialized | undefined>;
	saveBlockSerialized: (block: BlockSerialized) => Promise<void>;
}

export type BlockAdapterResolver = () => BlockAdapter | null;

const noBlockAdapter = () => null;
let resolveBlockAdapter: BlockAdapterResolver = noBlockAdapter;

export function setBlockAdapterResolver(resolver: BlockAdapterResolver): () => void {
	resolveBlockAdapter = resolver;

	return () => {
		if (resolveBlockAdapter === resolver) resolveBlockAdapter = noBlockAdapter;
	};
}

/** Captures one adapter so every operation from this consumer keeps the same runtime owner. */
export function useBlockAdapter() {
	const adapter = resolveBlockAdapter();

	async function loadBlockSerialized(id: string): Promise<BlockSerialized> {
		if (!adapter) {
			throw new Error('No block adapter registered for loadBlockSerialized');
		}

		const block = await adapter.loadBlockSerialized(id);

		if (!block) {
			throw new BlockNotFoundError(id);
		}

		return block;
	}

	async function saveBlockSerialized(block: BlockSerialized): Promise<void> {
		if (!adapter) {
			throw new Error('No block adapter registered for saveBlockSerialized');
		}

		await adapter.saveBlockSerialized(block);
	}

	return {
		loadBlockSerialized,
		saveBlockSerialized,
	};
}
